import { useState, useMemo } from 'react';
import { COLUMN_MAP, FIELD_LABELS, findColumnIndex, normalizeHeader } from './csvUtils';

interface MapeamentoStepProps {
  rawHeaders: string[];
  initialMapping: Record<string, number>; // campo BD → índice coluna CSV (-1 = não mapeado)
  onConfirm: (mapping: Record<string, number>) => void;
  onBack: () => void;
}

// Campos que aparecem no mapeamento (os mais relevantes)
const FIELDS_TO_MAP = [
  'id', 'nome', 'carreira', 'sexo', 'nivel_atual', 'novo_nivel_2025',
  'vencimento', 'avaliacao', 'ultima_progressao', 'data_entrada_funcao_publica',
  'unidade_organica', 'subunidade_organica', 'pontos_atuais', 'email',
  'telefone', 'data_nascimento', 'cpf', 'endereco', 'status',
  'superior_hierarquico', 'categoria', 'despacho_autorizacao', 'certificados',
  'descricao_subida',
];

const REQUIRED_FIELDS = ['id', 'nome'];

export default function MapeamentoStep({ rawHeaders, initialMapping, onConfirm, onBack }: MapeamentoStepProps) {
  const [mapping, setMapping] = useState<Record<string, number>>(initialMapping);
  const [showAll, setShowAll] = useState(false);

  // Colunas CSV não mapeadas para nenhum campo
  const unmappedCsvCols = useMemo(() => {
    const usedIndices = new Set(Object.values(mapping).filter((v) => v !== -1));
    return rawHeaders
      .map((h, i) => ({ header: h, index: i }))
      .filter(({ index }) => !usedIndices.has(index));
  }, [mapping, rawHeaders]);

  const autoDetectedCount = useMemo(
    () => Object.values(initialMapping).filter((v) => v !== -1).length,
    [initialMapping],
  );

  const missingRequired = REQUIRED_FIELDS.filter((f) => mapping[f] === -1 || mapping[f] === undefined);

  const fieldsToShow = showAll ? FIELDS_TO_MAP : FIELDS_TO_MAP.filter(
    (f) => REQUIRED_FIELDS.includes(f) || (mapping[f] !== undefined && mapping[f] !== -1),
  );

  const handleChange = (field: string, colIndex: number) => {
    setMapping((prev) => {
      const next = { ...prev };
      // Se outra coluna já estava mapeada para este índice, desassociar
      if (colIndex !== -1) {
        for (const [k, v] of Object.entries(next)) {
          if (v === colIndex && k !== field) next[k] = -1;
        }
      }
      next[field] = colIndex;
      return next;
    });
  };

  return (
    <div className="p-6 space-y-5">
      {/* Cabeçalho informativo */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <i className="ri-map-pin-line text-amber-500 text-base flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-[13px] font-semibold text-amber-800">Mapeamento de colunas</p>
          <p className="text-[12px] text-amber-700 mt-0.5">
            O importador detetou automaticamente <strong>{autoDetectedCount}</strong> de {FIELDS_TO_MAP.length} campos.
            Associa manualmente as colunas que não foram reconhecidas.
          </p>
        </div>
      </div>

      {/* Colunas CSV não mapeadas */}
      {unmappedCsvCols.length > 0 && (
        <div className="bg-zinc-50 rounded-xl p-3">
          <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-2">
            Colunas do CSV ainda não associadas ({unmappedCsvCols.length})
          </p>
          <div className="flex flex-wrap gap-1.5">
            {unmappedCsvCols.map(({ header, index }) => (
              <span key={index} className="text-[11px] font-mono bg-zinc-200 text-zinc-600 px-2 py-0.5 rounded-md">
                {header}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Tabela de mapeamento */}
      <div className="border border-zinc-100 rounded-xl overflow-hidden">
        <div className="bg-zinc-50 px-4 py-2.5 flex items-center justify-between border-b border-zinc-100">
          <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest">
            Campo da base de dados → Coluna do CSV
          </p>
          <button
            onClick={() => setShowAll((v) => !v)}
            className="text-[11px] text-zinc-400 hover:text-zinc-700 cursor-pointer transition-colors"
          >
            {showAll ? 'Mostrar só relevantes' : `Mostrar todos os ${FIELDS_TO_MAP.length} campos`}
          </button>
        </div>
        <div className="divide-y divide-zinc-50 max-h-72 overflow-y-auto">
          {fieldsToShow.map((field) => {
            const isRequired = REQUIRED_FIELDS.includes(field);
            const currentIdx = mapping[field] ?? -1;
            const isMapped = currentIdx !== -1;
            return (
              <div key={field} className={`flex items-center gap-3 px-4 py-2.5 ${!isMapped && isRequired ? 'bg-red-50/50' : ''}`}>
                <div className="w-48 flex-shrink-0">
                  <span className="text-[12px] font-medium text-zinc-700">{FIELD_LABELS[field] || field}</span>
                  {isRequired && (
                    <span className="ml-1.5 text-[10px] text-red-500 font-semibold">obrig.</span>
                  )}
                </div>
                <div className="flex-1">
                  <select
                    value={currentIdx}
                    onChange={(e) => handleChange(field, parseInt(e.target.value, 10))}
                    className={`w-full text-[12px] border rounded-lg px-2.5 py-1.5 cursor-pointer outline-none transition-colors ${
                      isMapped
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                        : isRequired
                          ? 'border-red-200 bg-red-50 text-red-600'
                          : 'border-zinc-200 bg-white text-zinc-500'
                    }`}
                  >
                    <option value={-1}>— Não mapear —</option>
                    {rawHeaders.map((h, i) => (
                      <option key={i} value={i}>
                        {h} {mapping[field] === i ? '✓' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-6 flex-shrink-0 flex items-center justify-center">
                  {isMapped
                    ? <i className="ri-checkbox-circle-fill text-emerald-500 text-base" />
                    : isRequired
                      ? <i className="ri-error-warning-fill text-red-400 text-base" />
                      : <i className="ri-subtract-line text-zinc-300 text-base" />
                  }
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Aviso campos obrigatórios em falta */}
      {missingRequired.length > 0 && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
          <i className="ri-error-warning-fill text-red-500 text-sm flex-shrink-0" />
          <p className="text-[12px] text-red-700">
            Campos obrigatórios sem mapeamento: <strong>{missingRequired.map((f) => FIELD_LABELS[f]).join(', ')}</strong>
          </p>
        </div>
      )}

      {/* Rodapé */}
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={onBack}
          className="whitespace-nowrap flex items-center gap-2 text-[13px] text-zinc-500 hover:text-zinc-800 border border-zinc-200 px-4 py-2 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line text-sm" />
          Voltar
        </button>
        <button
          onClick={() => onConfirm(mapping)}
          disabled={missingRequired.length > 0}
          className="whitespace-nowrap flex items-center gap-2 px-5 py-2 bg-zinc-900 text-white text-[13px] font-medium rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-40"
        >
          Confirmar mapeamento
          <i className="ri-arrow-right-line text-sm" />
        </button>
      </div>
    </div>
  );
}
